import type { ReferenceImageItem } from '../api/referenceImages'
import { resolveGalleryRemoteIngestUrl } from './referenceImageIngestUrl'
import {
  classifyReferenceImageLink,
  coerceUploadUrlForDevBrowser,
  type ReferenceImageLinkKind,
  type ReferenceImageUrlHints,
  resolveReferenceImageDisplayUrl,
} from './referenceImageUrl'

/**
 * 媒体图统一解析：展示、复制、保存、服务端代拉共用，避免「线上展示 / 本地代拉」不一致。
 */
export type ResolvedMediaImage = {
  raw: string
  /** 浏览器 img / 灯箱展示 */
  displayUrl: string
  /** POST image-bytes / 图库 from-remote（保留库内外链协议与生产域） */
  ingestUrl: string
  kind: ReferenceImageLinkKind
}

/** 浏览器展示用地址（img / 背景图）；统一走 {@link resolveMediaImage} */
export function mediaDisplayUrl(raw: string, hints?: ReferenceImageUrlHints): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  return resolveMediaImage(trimmed, hints).displayUrl
}

export function resolveMediaImage(
  raw: string,
  hints?: ReferenceImageUrlHints,
): ResolvedMediaImage {
  const trimmed = raw.trim()
  const resolved = resolveReferenceImageDisplayUrl(trimmed, hints)
  // 开发：站内绝对 URL 收成 /uploads 相对路径走 Vite 反代；生产：保留绝对 URL 或同源相对路径
  const displayUrl = import.meta.env.DEV
    ? coerceUploadUrlForDevBrowser(resolved)
    : resolved
  return {
    raw: trimmed,
    displayUrl,
    ingestUrl: resolveGalleryRemoteIngestUrl(trimmed, hints),
    kind: classifyReferenceImageLink(trimmed, hints),
  }
}

/** 图库条目 → URL 解析 hints（列表/灯箱/选择器共用） */
export function referenceImageHintsFromItem(
  item: Pick<ReferenceImageItem, 'bucket' | 'objectKey'>,
): ReferenceImageUrlHints {
  return { bucket: item.bucket, objectKey: item.objectKey }
}

/** 图库条目统一解析：展示、代拉、链路分类与 {@link resolveMediaImage} 一致 */
export function resolveReferenceImageFromItem(item: ReferenceImageItem): ResolvedMediaImage {
  return resolveMediaImage(item.url, referenceImageHintsFromItem(item))
}

export type ImageLightboxLikeItem = {
  src: string
  referenceImageUrls?: string[]
}

/** 灯箱条目：展示地址与操作地址对齐 */
export function normalizeImageLightboxItem<T extends ImageLightboxLikeItem>(item: T): T {
  const main = resolveMediaImage(item.src)
  return {
    ...item,
    src: main.displayUrl,
    referenceImageUrls: item.referenceImageUrls?.map((u) => resolveMediaImage(u).displayUrl),
  }
}
